import { QueryClient } from '@tanstack/react-query';

/**
 * REST client for everything that is *not* realtime: server browser, health,
 * and later accounts, progression, loadouts and match history. Realtime state
 * never goes through React Query — it arrives over the game socket.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const BASE = import.meta.env.VITE_API_URL ?? '/api';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const api = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...init?.headers },
  });
  if (!response.ok) {
    // The server sends { error } with a human-readable reason; surface it so
    // forms can show "that callsign is taken" instead of a status code.
    const reason = await response
      .json()
      .then((body: { error?: string }) => body.error)
      .catch(() => undefined);
    throw new ApiError(
      reason ?? `${init?.method ?? 'GET'} ${path} failed`,
      response.status,
    );
  }
  return (await response.json()) as T;
};

export interface ServerListing {
  id: string;
  name: string;
  map: string;
  mapName: string;
  tagline: string;
  phase: string;
  players: number;
  maxPlayers: number;
  ws: string;
}

export const serverListQuery = {
  queryKey: ['servers'] as const,
  queryFn: () => api<ServerListing[]>('/servers'),
  refetchInterval: 5000,
};

// ---------------------------------------------------------------------------
// Accounts — the session token lives in localStorage, sent as a bearer header
// ---------------------------------------------------------------------------

const TOKEN_KEY = 'callout:session';

export const getSessionToken = (): string | null => localStorage.getItem(TOKEN_KEY);
export const setSessionToken = (token: string): void =>
  localStorage.setItem(TOKEN_KEY, token);
export const clearSessionToken = (): void => localStorage.removeItem(TOKEN_KEY);

const authHeaders = (): Record<string, string> => {
  const token = getSessionToken();
  return token ? { authorization: `Bearer ${token}` } : {};
};

export interface AccountInfo {
  id: string;
  name: string;
  email: string;
  verified: boolean;
}

export interface ProfileInfo {
  level: number;
  xp: number;
  matchesPlayed: number;
  wins: number;
  kills: number;
  deaths: number;
}

export interface AuthResponse {
  token: string;
  account: AccountInfo;
  profile: ProfileInfo | null;
}

export interface MeResponse {
  account: AccountInfo;
  profile: ProfileInfo | null;
}

export const register = async (
  name: string,
  email: string,
  password: string,
): Promise<AuthResponse> => {
  const response = await api<AuthResponse>('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ name, email, password }),
  });
  setSessionToken(response.token);
  return response;
};

export const login = async (email: string, password: string): Promise<AuthResponse> => {
  const response = await api<AuthResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  setSessionToken(response.token);
  return response;
};

/** Confirms the address behind an emailed ?verify= link. */
export const verifyEmail = (token: string): Promise<{ ok: boolean }> =>
  api('/auth/verify', { method: 'POST', body: JSON.stringify({ token }) });

/** Asks for a fresh verification email for the signed-in account. */
export const requestVerification = (): Promise<{ ok: boolean }> =>
  api('/auth/verify/request', { method: 'POST', headers: authHeaders() });

/** Always resolves ok — the server never reveals whether the email exists. */
export const requestPasswordReset = (email: string): Promise<{ ok: boolean }> =>
  api('/auth/reset/request', { method: 'POST', body: JSON.stringify({ email }) });

/** Redeems an emailed ?reset= link; a success signs the player in. */
export const resetPassword = async (
  token: string,
  password: string,
): Promise<AuthResponse> => {
  const response = await api<AuthResponse>('/auth/reset', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
  setSessionToken(response.token);
  return response;
};

export const logout = async (): Promise<void> => {
  // Revoke best-effort; the local token is gone either way.
  await api('/auth/logout', { method: 'POST', headers: authHeaders() }).catch(() => {});
  clearSessionToken();
};

// ---------------------------------------------------------------------------
// Game identity — who the socket says we are
// ---------------------------------------------------------------------------

const GUEST_KEY = 'callout:guest';

/**
 * A random handle guests carry across reloads, so the server can hold their
 * seat and block double-tabs even without an account.
 */
export const getGuestHandle = (): string => {
  let handle = localStorage.getItem(GUEST_KEY);
  if (!handle) {
    handle = crypto.randomUUID().replaceAll('-', '');
    localStorage.setItem(GUEST_KEY, handle);
  }
  return handle;
};

/** The session string the Join frame carries: account token or guest handle. */
export const gameSession = (): string =>
  getSessionToken() ?? `guest:${getGuestHandle()}`;

export interface ActiveSession {
  state: 'none' | 'reserved' | 'connected';
  matchId?: string;
  remainingMs?: number;
}

const sessionAuth = (): { path: string; headers: Record<string, string> } => {
  const token = getSessionToken();
  return token
    ? { path: '', headers: { authorization: `Bearer ${token}` } }
    : { path: `?guest=${getGuestHandle()}`, headers: {} };
};

export const fetchActiveSession = async (): Promise<ActiveSession> => {
  const { path, headers } = sessionAuth();
  return api<ActiveSession>(`/session/active${path}`, { headers });
};

export const abandonActiveSession = async (): Promise<void> => {
  const { path, headers } = sessionAuth();
  await api(`/session/abandon${path}`, { method: 'POST', headers }).catch(() => {});
};

export interface LeaderboardEntry {
  name: string;
  wins: number;
  kills: number;
  deaths: number;
  matchesPlayed: number;
}

export const leaderboardQuery = {
  queryKey: ['leaderboard'] as const,
  queryFn: () => api<LeaderboardEntry[]>('/leaderboard'),
  staleTime: 30_000,
};

export const meQuery = {
  queryKey: ['me'] as const,
  queryFn: async (): Promise<MeResponse | null> => {
    if (!getSessionToken()) return null;
    try {
      return await api<MeResponse>('/auth/me', { headers: authHeaders() });
    } catch (error) {
      // An expired or revoked session is a normal signed-out state, not an error.
      if (error instanceof ApiError && error.status === 401) {
        clearSessionToken();
        return null;
      }
      throw error;
    }
  },
  staleTime: 60_000,
};
